import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { COLUNAS_KANBAN } from './constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useBolsaTarefas() {
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operadores, setOperadores] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [operacoesDisponiveis, setOperacoesDisponiveis] = useState([]);
  const [categoriasTarefas, setCategoriasTarefas] = useState([]);
  const [conclusoes, setConclusoes] = useState([]);
  const [filtroCategoria, setFiltroCategoria] = useState('todas');

  const [operacoesSugeridas, setOperacoesSugeridas] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configOperacoes, setConfigOperacoes] = useState([]);

  const [showDialog, setShowDialog] = useState(false);
  const [editingTarefa, setEditingTarefa] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    marca: '',
    modelo: '',
    observacoes: '',
    urgente: false,
    operacoes: [],
    operadores_ids: [],
    estado: 'pendente',
    categoria_id: ''
  });
  const [marcaTextoLivre, setMarcaTextoLivre] = useState(false);

  const [draggedTarefa, setDraggedTarefa] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const [showArquivadas, setShowArquivadas] = useState(false);

  const [showCategoriasModal, setShowCategoriasModal] = useState(false);
  const [novaCatNome, setNovaCatNome] = useState('');
  const [novaCatCor, setNovaCatCor] = useState('#6366f1');
  const [editingCat, setEditingCat] = useState(null);

  const [showOperacoesSelector, setShowOperacoesSelector] = useState(false);

  // Operadores filtrados por permissao das operacoes selecionadas
  const operadoresFiltrados = useMemo(() => {
    if (!formData.operacoes || formData.operacoes.length === 0) return [];
    const operacoesIds = new Set(formData.operacoes.map(op => op.operacao_id));
    return operadores.filter(operador => {
      if (operador.ativo === false) return false;
      const permissoesCategorias = operador.permissoes_categorias || {};
      const todasOperacoesPermitidas = Object.values(permissoesCategorias).flat();
      return [...operacoesIds].some(opId => todasOperacoesPermitidas.includes(opId));
    });
  }, [formData.operacoes, operadores]);

  // Carregar dados
  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const [tarefasRes, operadoresRes, operacoesRes, categoriasRes, conclusoesRes] = await Promise.all([
        axios.get(`${API}/tarefas?incluir_arquivados=true`),
        axios.get(`${API}/operadores`),
        axios.get(`${API}/operacoes`),
        axios.get(`${API}/tarefas/categorias/lista`).catch(() => ({ data: [] })),
        axios.get(`${API}/tarefas/conclusoes/lista`).catch(() => ({ data: [] }))
      ]);
      setTarefas(tarefasRes.data);
      setOperadores(operadoresRes.data);
      setOperacoesDisponiveis(operacoesRes.data);
      setCategoriasTarefas(categoriasRes.data || []);
      setConclusoes(conclusoesRes.data || []);
      try {
        const marcasRes = await axios.get(`${API}/clientes`);
        setMarcas(marcasRes.data || []);
      } catch { setMarcas([]); }
      try {
        const sugeridasRes = await axios.get(`${API}/tarefas/configuracao/operacoes-sugeridas`);
        setOperacoesSugeridas(sugeridasRes.data || []);
      } catch { setOperacoesSugeridas([]); }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // Limpar operadores invalidos quando operacoes mudam
  useEffect(() => {
    if (formData.operadores_ids.length > 0 && operadoresFiltrados.length > 0) {
      const filtradosIds = new Set(operadoresFiltrados.map(op => op.id));
      const validos = formData.operadores_ids.filter(id => filtradosIds.has(id));
      if (validos.length !== formData.operadores_ids.length) {
        setFormData(prev => ({ ...prev, operadores_ids: validos }));
      }
    }
  }, [operadoresFiltrados, formData.operadores_ids]);

  const descarregarQRCode = () => window.open('/cartaz-pontuais', '_blank');

  const abrirNovaTarefa = () => {
    setFormData({
      nome: '', marca: '', modelo: '', observacoes: '', urgente: false, obriga_contagem: false,
      operacoes: [], operadores_ids: [], estado: 'pendente', categoria_id: ''
    });
    setMarcaTextoLivre(false);
    setEditingTarefa(null);
    setShowDialog(true);
  };

  const abrirEditarTarefa = (tarefa) => {
    const marcaExisteNaLista = marcas.some(m => m.nome === tarefa.marca);
    setMarcaTextoLivre(tarefa.marca && !marcaExisteNaLista);
    setFormData({
      nome: tarefa.nome || '', marca: tarefa.marca || '', modelo: tarefa.modelo || '',
      observacoes: tarefa.observacoes || '', urgente: tarefa.urgente || false,
      obriga_contagem: tarefa.obriga_contagem || false,
      operacoes: tarefa.operacoes || [],
      operadores_ids: tarefa.operadores_ids || (tarefa.operador_id ? [tarefa.operador_id] : []),
      estado: tarefa.estado || 'pendente', categoria_id: tarefa.categoria_id || ''
    });
    setEditingTarefa(tarefa);
    setShowDialog(true);
  };

  const guardarTarefa = async () => {
    if (formData.operacoes.length === 0) { toast.error('Selecione pelo menos uma operacao'); return; }
    if (!formData.operadores_ids || formData.operadores_ids.length === 0) { toast.error('Selecione pelo menos um operador'); return; }
    // Auto-gerar nome a partir das operações se vazio
    const nomeAuto = formData.nome?.trim() || formData.operacoes.map(o => `${o.operacao_codigo} ${o.operacao_nome}`).join(' + ');
    const payload = { ...formData, nome: nomeAuto };
    try {
      if (editingTarefa) {
        await axios.put(`${API}/tarefas/${editingTarefa.id}`, payload);
        toast.success('Tarefa atualizada');
      } else {
        await axios.post(`${API}/tarefas`, payload);
        toast.success('Tarefa criada');
      }
      setShowDialog(false);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao guardar tarefa');
      console.error(error);
    }
  };

  const eliminarTarefa = async (tarefaId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar esta tarefa?')) return;
    try {
      await axios.delete(`${API}/tarefas/${tarefaId}`);
      toast.success('Tarefa eliminada');
      carregarDados();
    } catch { toast.error('Erro ao eliminar tarefa'); }
  };

  const arquivarTarefa = async (tarefaId) => {
    try {
      await axios.put(`${API}/tarefas/${tarefaId}`, { estado: 'arquivado' });
      toast.success('Tarefa arquivada');
      carregarDados();
    } catch { toast.error('Erro ao arquivar tarefa'); }
  };

  const desarquivarTarefa = async (tarefaId) => {
    try {
      await axios.put(`${API}/tarefas/${tarefaId}`, { estado: 'pendente' });
      toast.success('Tarefa desarquivada');
      carregarDados();
    } catch { toast.error('Erro ao desarquivar tarefa'); }
  };

  const duplicarTarefa = (tarefa) => {
    setFormData({
      nome: `Copia - ${tarefa.nome}`, marca: tarefa.marca || '', modelo: tarefa.modelo || '',
      observacoes: tarefa.observacoes || '', urgente: tarefa.urgente || false,
      obriga_contagem: tarefa.obriga_contagem || false,
      operacoes: tarefa.operacoes || [],
      operadores_ids: tarefa.operadores_ids || (tarefa.operador_id ? [tarefa.operador_id] : []),
      estado: 'pendente', categoria_id: tarefa.categoria_id || ''
    });
    const marcaExisteNaLista = marcas.some(m => m.nome === tarefa.marca);
    setMarcaTextoLivre(tarefa.marca && !marcaExisteNaLista);
    setEditingTarefa(null);
    setShowDialog(true);
    toast.info('A duplicar tarefa - ajuste os dados e guarde');
  };

  const toggleOperacaoSugerida = (opSugerida) => {
    const opCompleta = operacoesDisponiveis.find(o => o.id === opSugerida.operacao_id);
    if (!opCompleta) { toast.error(`Operacao ${opSugerida.operacao_codigo} nao encontrada no sistema`); return; }
    const existe = formData.operacoes.find(o => o.operacao_id === opSugerida.operacao_id);
    if (existe) {
      setFormData(prev => ({ ...prev, operacoes: prev.operacoes.filter(o => o.operacao_id !== opSugerida.operacao_id) }));
    } else {
      setFormData(prev => ({
        ...prev,
        operacoes: [...prev.operacoes, {
          operacao_id: opCompleta.id, operacao_codigo: opCompleta.codigo,
          operacao_nome: opCompleta.nome, tempo_standard: opCompleta.tempo_standard || 0
        }],
        // Auto-marca "obriga contagem" se a operação adicionada já é de contagem no catálogo
        obriga_contagem: prev.obriga_contagem || !!opCompleta.is_contagem
      }));
    }
  };

  const abrirConfigModal = () => { setConfigOperacoes([...operacoesSugeridas]); setShowConfigModal(true); };

  const adicionarOperacaoConfig = (operacao) => {
    if (configOperacoes.find(o => o.operacao_id === operacao.id)) { toast.info('Esta operacao ja esta nas sugestoes'); return; }
    setConfigOperacoes(prev => [...prev, {
      operacao_id: operacao.id, operacao_codigo: operacao.codigo,
      operacao_nome: operacao.nome, tempo_standard: operacao.tempo_standard || 0
    }]);
  };

  const removerOperacaoConfig = (index) => { setConfigOperacoes(prev => prev.filter((_, i) => i !== index)); };

  const guardarConfigOperacoes = async () => {
    try {
      await axios.post(`${API}/tarefas/configuracao/operacoes-sugeridas`, { operacoes_sugeridas: configOperacoes });
      setOperacoesSugeridas(configOperacoes);
      setShowConfigModal(false);
      toast.success('Operacoes sugeridas guardadas');
    } catch (error) { toast.error('Erro ao guardar configuracao'); console.error(error); }
  };

  const adicionarOperacao = (operacao) => {
    if (formData.operacoes.find(o => o.operacao_id === operacao.id)) { toast.info('Esta operacao ja esta selecionada'); return; }
    setFormData(prev => ({
      ...prev,
      operacoes: [...prev.operacoes, {
        operacao_id: operacao.id, operacao_codigo: operacao.codigo,
        operacao_nome: operacao.nome, tempo_standard: operacao.tempo_standard || 0
      }],
      // Auto-marca "obriga contagem" se a operação adicionada já é de contagem no catálogo
      obriga_contagem: prev.obriga_contagem || !!operacao.is_contagem
    }));
    setShowOperacoesSelector(false);
  };

  const removerOperacao = (index) => { setFormData(prev => ({ ...prev, operacoes: prev.operacoes.filter((_, i) => i !== index) })); };

  // Drag and Drop
  const handleDragStart = (e, tarefa) => { setDraggedTarefa(tarefa); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e, coluna) => { e.preventDefault(); setDragOverColumn(coluna); };
  const handleDragLeave = () => { setDragOverColumn(null); };
  const handleDrop = async (e, novoEstado) => {
    e.preventDefault();
    setDragOverColumn(null);
    if (!draggedTarefa || draggedTarefa.estado === novoEstado) { setDraggedTarefa(null); return; }
    try {
      const tarefasNaColuna = tarefas.filter(t => t.estado === novoEstado);
      const novaOrdem = tarefasNaColuna.length > 0 ? Math.max(...tarefasNaColuna.map(t => t.ordem || 0)) + 1 : 1;
      await axios.put(`${API}/tarefas/${draggedTarefa.id}`, { estado: novoEstado, ordem: novaOrdem });
      toast.success(`Tarefa movida para ${COLUNAS_KANBAN[novoEstado].label}`);
      carregarDados();
    } catch { toast.error('Erro ao mover tarefa'); }
    setDraggedTarefa(null);
  };
  const handleDragEnd = () => { setDraggedTarefa(null); setDragOverColumn(null); };

  // Categorias
  const criarCategoria = async () => {
    if (!novaCatNome.trim()) return;
    try {
      await axios.post(`${API}/tarefas/categorias/lista`, { nome: novaCatNome.trim(), cor: novaCatCor });
      toast.success('Categoria criada');
      setNovaCatNome('');
      setNovaCatCor('#6366f1');
      carregarDados();
    } catch { toast.error('Erro ao criar categoria'); }
  };

  const atualizarCategoria = async (catId, dados) => {
    try {
      await axios.put(`${API}/tarefas/categorias/${catId}`, dados);
      toast.success('Categoria atualizada');
      setEditingCat(null);
      carregarDados();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const eliminarCategoria = async (catId) => {
    try {
      await axios.delete(`${API}/tarefas/categorias/${catId}`);
      toast.success('Categoria eliminada');
      if (filtroCategoria === catId) setFiltroCategoria('todas');
      carregarDados();
    } catch { toast.error('Erro ao eliminar'); }
  };

  // Tarefas filtradas e agrupadas
  const tarefasFiltradas = useMemo(() => {
    if (filtroCategoria === 'todas') return tarefas;
    if (filtroCategoria === 'sem_categoria') return tarefas.filter(t => !t.categoria_id);
    return tarefas.filter(t => t.categoria_id === filtroCategoria);
  }, [tarefas, filtroCategoria]);

  const tarefasPorEstado = useMemo(() => ({
    pendente: tarefasFiltradas.filter(t => t.estado === 'pendente').sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    producao: tarefasFiltradas.filter(t => t.estado === 'producao').sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    conclusoes: filtroCategoria === 'todas'
      ? conclusoes
      : conclusoes.filter(c => c.tarefa_categoria_id === filtroCategoria || (!c.tarefa_categoria_id && filtroCategoria === 'sem_categoria')),
    arquivado: tarefasFiltradas.filter(t => t.estado === 'arquivado').sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
  }), [tarefasFiltradas, conclusoes, filtroCategoria]);

  return {
    // Data
    loading, tarefas, operadores, marcas, operacoesDisponiveis, categoriasTarefas, conclusoes,
    tarefasPorEstado, operadoresFiltrados,
    // Filtro
    filtroCategoria, setFiltroCategoria,
    // Arquivadas
    showArquivadas, setShowArquivadas,
    // Dialog tarefa
    showDialog, setShowDialog, editingTarefa, formData, setFormData,
    // Operacoes
    operacoesSugeridas, showOperacoesSelector, setShowOperacoesSelector,
    // Config modal
    showConfigModal, setShowConfigModal, configOperacoes,
    // Categorias modal
    showCategoriasModal, setShowCategoriasModal, novaCatNome, setNovaCatNome, novaCatCor, setNovaCatCor, editingCat, setEditingCat,
    // Drag
    draggedTarefa, dragOverColumn,
    // Actions
    carregarDados, descarregarQRCode,
    abrirNovaTarefa, abrirEditarTarefa, guardarTarefa, eliminarTarefa,
    arquivarTarefa, desarquivarTarefa, duplicarTarefa,
    toggleOperacaoSugerida, abrirConfigModal, adicionarOperacaoConfig, removerOperacaoConfig, guardarConfigOperacoes,
    adicionarOperacao, removerOperacao,
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    criarCategoria, atualizarCategoria, eliminarCategoria
  };
}
